import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AttrIcon, ordinal, tagDetail } from "@/components/AttributesPanel";
import { SPORTS } from "@/constants/sports";
import { api } from "@/lib/api";

const ATTR_SPORTS = ["softball", "basketball", "volleyball"];
const PAGE = 25;

const Row = ({ p, i, sport }) => (
  <li>
    <Link to={`/players/${p.id}`} data-testid={`attr-leader-${i + 1}`}
      className={`flex items-center gap-4 px-5 py-3 transition-colors duration-200 hover:bg-[#0B0F19]/70 ${i === 0 ? "bg-[#FF2D78]/10" : ""}`}>
      <span className={`w-8 font-display text-2xl font-black ${i < 3 ? "text-[#FF2D78]" : "text-slate-600"}`}>{i + 1}</span>
      {p.team_logo ? <img src={p.team_logo} alt="" className="h-7 w-7 shrink-0 object-contain" /> : <span className="h-7 w-7 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-white">{p.name}</span>
        <span className="block truncate text-xs text-slate-500">{[p.team, p.position].filter(Boolean).join(" · ")}</span>
      </span>
      <span className="hidden max-w-[45%] truncate font-data text-xs text-slate-400 md:block">{tagDetail({ ...p.tag, percentile: null }, sport)}</span>
      <span className="w-16 text-right font-data text-lg font-bold text-white">{p.tag.percentile != null ? ordinal(p.tag.percentile) : "—"}</span>
    </Link>
  </li>
);

export const AttributeLeaders = () => {
  const [sport, setSport] = useState("softball");
  const [meta, setMeta] = useState(null);
  const [tag, setTag] = useState(null);
  const [conference, setConference] = useState("all");
  const [data, setData] = useState(null);
  const [more, setMore] = useState(false);

  useEffect(() => { api.get("/players/meta").then((r) => setMeta(r.data.attributes)); }, []);
  const tags = meta?.[sport]?.tags || [];
  useEffect(() => { setTag(tags[0]?.key || null); setConference("all"); }, [sport, meta]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!tag) return;
    let stale = false;
    setData(null);
    api.get("/attribute-leaders", { params: { sport, tag, limit: PAGE, ...(conference !== "all" ? { conference } : {}) } })
      .then((r) => { if (!stale) setData(r.data); });
    return () => { stale = true; };
  }, [sport, tag, conference]);

  const loadMore = async () => {
    setMore(true);
    const r = await api.get("/attribute-leaders", { params: { sport, tag, limit: PAGE, skip: data.items.length, ...(conference !== "all" ? { conference } : {}) } });
    setData((d) => ({ ...d, items: [...d.items, ...r.data.items] }));
    setMore(false);
  };
  const current = tags.find((t) => t.key === tag);

  return (
    <div data-testid="attribute-leaders">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap gap-1 rounded-lg border border-slate-800 bg-[#131927] p-1">
          {ATTR_SPORTS.map((k) => (
            <button key={k} data-testid={`attr-leaders-sport-${k}`} onClick={() => setSport(k)}
              className={`rounded-md px-5 py-2 font-data text-[11px] uppercase tracking-wider transition-colors duration-200 ${sport === k ? "bg-[#FF2D78] text-white" : "text-slate-400 hover:text-white"}`}>
              {SPORTS[k].label} {meta?.[k]?.season}
            </button>
          ))}
        </div>
        <Select value={conference} onValueChange={setConference}>
          <SelectTrigger data-testid="attr-leaders-conference-select" className="h-11 w-64 border-slate-800 bg-[#131927]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80 border-slate-800 bg-[#131927]">
            <SelectItem value="all">All conferences</SelectItem>
            {(data?.conferences || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {tags.map((t) => (
          <button key={t.key} data-testid={`attr-leaders-tag-${t.key}`} onClick={() => setTag(t.key)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ${tag === t.key ? "border-[#FF2D78] bg-[#FF2D78]/20 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
            <span className="text-[#FF2D78]"><AttrIcon file={t.icon} sport={sport} className="h-3.5 w-3.5" /></span>{t.label}
          </button>
        ))}
      </div>
      <section className="rise mt-8 overflow-hidden rounded-xl border border-slate-800 bg-[#131927]">
        <div className="flex items-baseline justify-between border-b border-slate-800 px-5 py-4">
          <h2 data-testid="attr-leaders-title" className="font-display text-2xl font-extrabold uppercase tracking-tight">{current?.label || "Attributes"}</h2>
          <span className="eyebrow">{data ? `${data.total} players · percentile` : ""}</span>
        </div>
        {!data && <div className="space-y-2 p-5">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 bg-[#0B0F19]" />)}</div>}
        {data && !data.items.length && <p className="p-5 text-sm text-slate-500">No players have this attribute in this conference.</p>}
        <ol>{data?.items.map((p, i) => <Row key={p.id} p={p} i={i} sport={sport} />)}</ol>
        {data && data.items.length < data.total && (
          <div className="border-t border-slate-800 p-4 text-center">
            <button data-testid="attr-leaders-show-more" onClick={loadMore} disabled={more}
              className="rounded-full border border-slate-700 px-6 py-2 font-data text-xs uppercase tracking-widest text-slate-200 transition-colors duration-200 hover:border-[#FF2D78] disabled:opacity-50">
              {more ? "Loading" : `Show more (${data.total - data.items.length} left)`}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};
